import { loadStripe } from "@stripe/stripe-js";
import useAPI from "@toothfairy/shared-api/useApi";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import envConfig from "../../../../envConfig";
import API_Consumption from "../../../API/API_Consumption";
import useCredits from "../../../Hooks/useCredits";
import BillingDetailsView from "./BillingDetailsView";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppUser from "@toothfairy/shared-ui/AppUser";
import {
  isBillingAddressSet,
  isFirstTrialStarted,
  isUserDelinquent,
  isUserEnabledToOperate,
  isUserOnTrial,
  isUserWithNoPaymentMethod,
  isSSO,
} from "../../../../App";
import useRechargeRedirect from "../../../Hooks/useRechargeRedirect";

const stripePromise = loadStripe(envConfig.REACT_APP_STRIPE_PK);

const BillingDetailsContainer = ({ isWizard }) => {
  const { t } = useTranslation();
  const { user } = AppUser.useUser();
  // Shared credits instance — lifted here so the header refresh button can
  // re-poll the live balance (BillingCredits + RechargeSheet both consume it).
  const credits = useCredits();
  // Detect a Stripe Checkout redirect and synchronously confirm the recharge.
  useRechargeRedirect();
  const [billingHistory, setBillingHistory] = useState([]);
  const [isPaymentMethodEditMode, setPaymentMethodEditMode] = useState(false);
  const AppToasty = AppToast.useToast();
  const {
    data: stripeCustomerData,
    error: stripeCustomerAPIError,
    loading: stripeCustomerAPIInProgress,
    apiRequest: fetchStripeCustomer,
  } = useAPI(API_Consumption.getStripeCustomer);

  const {
    data: stripeClientSecretData,
    error: stripeClientSecretAPIError,
    loading: stripeClientSecretAPIInProgress,
    apiRequest: fetchStripeClientSecret,
  } = useAPI(API_Consumption.getStripeClientSecret);

  const {
    data: stripeClientBillingHistoryData,
    error: stripeClientBillingHistoryAPIError,
    loading: stripeClientBillingHistoryAPIInProgress,
    apiRequest: fetchStripeClientBillingHistory,
  } = useAPI(API_Consumption.getStripeClientBillingHistory);

  const {
    loading: clientPaymentIntentAPIInProgress,
    apiRequest: createClientPaymentIntent,
  } = useAPI(API_Consumption.createClientPaymentIntent);

  const {
    loading: paymentMethodAPIInProgress,
    apiRequest: deletePaymentMethod,
  } = useAPI(API_Consumption.deleteClientPaymentMethod);

  const { apiRequest: fetchStripeCheckout } = useAPI(
    API_Consumption.getStripeCheckout
  );
  const { apiRequest: saveStripeCustomer, loading: saveInProgress } = useAPI(
    API_Consumption.saveStripeCustomer
  );
  const {
    data: stripePortalData,
    error: stripePortalAPIError,
    loading: stripePortalAPIInProgress,
    apiRequest: fetchStripePortal,
  } = useAPI(API_Consumption.getStripePortal);

  const {
    data: paymentMethodsData,
    error: paymentMethodsAPIError,
    loading: paymentMethodsAPIInProgress,
    apiRequest: fetchPaymentMethods,
  } = useAPI(API_Consumption.getClientPaymentMethods);
  const setupCustomStripeDetails = (resultFromStripe) => {
    if (resultFromStripe) {
      isUserDelinquent(resultFromStripe.delinquent);
      isFirstTrialStarted(resultFromStripe.trialEndDate != null);
      isBillingAddressSet(resultFromStripe.address !== null);
      isUserOnTrial(
        resultFromStripe.trialEndDate !== null &&
          resultFromStripe.trialEndDate * 1000 > Date.now()
      );
      isUserWithNoPaymentMethod(
        resultFromStripe.invoice_settings.default_payment_method === null
      );
      isUserEnabledToOperate(
        !resultFromStripe.delinquent &&
          resultFromStripe.invoice_settings.default_payment_method !== null
      );
    }
  };
  useEffect(() => {
    if (stripeCustomerAPIError)
      AppToasty.show(t("StripeCustomerFetchFailed"), { type: "danger" });
    if (stripeClientSecretAPIError)
      AppToasty.show(t("StripeClientSecretFetchFailed"), { type: "danger" });
    if (stripeClientBillingHistoryAPIError)
      AppToasty.show(t("StripeClientInvoicesFetchFailed"), { type: "danger" });
    if (stripePortalAPIError)
      AppToasty.show(t("StripePortalFetchFailed"), { type: "danger" });
    if (paymentMethodsAPIError)
      AppToasty.show(t("PaymentMethodsFetchFailed"), { type: "danger" });
  }, [
    stripeCustomerAPIError,
    stripeClientSecretAPIError,
    stripeClientBillingHistoryAPIError,
    stripePortalAPIError,
  ]);
  const onPaymentMethodSave = async (pm_id) => {
    await saveStripeCustomer({
      invoice_settings: {
        default_payment_method: pm_id,
      },
    });
    await fetchPaymentMethods();
    const resultFromStripe = await fetchStripeCustomer();
    if (resultFromStripe) {
      setupCustomStripeDetails(resultFromStripe);
    }
  };
  // Set a specific attached card as the primary (default_payment_method).
  const setPrimaryPaymentMethod = async (pm_id) => {
    try {
      await onPaymentMethodSave(pm_id);
      AppToasty.show(t("billing.paymentMethodPrimarySet"), { type: "success" });
    } catch (err) {
      console.error("Failed to set primary payment method", err);
      AppToasty.show(t("billing.paymentMethodPrimarySetFailed"), { type: "danger" });
    }
  };
  // Detach (remove) a specific card. Refuses to remove the last remaining card
  // so the customer always keeps at least one on file.
  const removePaymentMethod = async (pm_id) => {
    try {
      const attached =
        paymentMethodsData?.paymentMethods?.data?.filter(
          (pm) => pm.id !== pm_id,
        ) || [];
      await deletePaymentMethod({ paymentMethodId: pm_id });
      // If we removed the primary, promote the first remaining card (if any).
      const removedWasPrimary =
        stripeCustomerData?.invoice_settings?.default_payment_method === pm_id;
      if (removedWasPrimary && attached.length > 0) {
        await onPaymentMethodSave(attached[0].id);
      } else {
        await fetchPaymentMethods();
        const resultFromStripe = await fetchStripeCustomer();
        if (resultFromStripe) setupCustomStripeDetails(resultFromStripe);
      }
      AppToasty.show(t("billing.paymentMethodRemoved"), { type: "success" });
    } catch (err) {
      console.error("Failed to remove payment method", err);
      AppToasty.show(t("billing.paymentMethodRemoveFailed"), { type: "danger" });
    }
  };
  const loadMoreBillingHistory = async () => {
    const lastId = billingHistory[billingHistory.length - 1]?.id;
    await fetchStripeClientBillingHistory(lastId);
  };
  const fetchCustomerAddress = async () => {
    try {
      await Promise.all([
        fetchStripeCustomer(),
        fetchStripeClientSecret(),
        fetchStripeClientBillingHistory(),
        fetchPaymentMethods(),
      ]);
    } catch (err) {
      console.error(err);
      AppToasty.show(t("StripeCustomerFetchFailed"), { type: "danger" });
    }
  };

  // Pull the latest Stripe data for the whole billing screen: customer (balance
  // + metadata config), setup secret, billing history and payment methods.
  // Surfaced via the header refresh button so a user can re-sync after paying
  // in the OS browser (Electron) or any external change.
  const refreshBillingData = async () => {
    await fetchCustomerAddress();
    // Re-poll the live credit balance so the "Credits & limits" section
    // reflects the new value immediately.
    try {
      await credits.refresh({ silent: true });
    } catch (e) {
      // swallow — fetchCustomerAddress already refreshed the customer object
    }
  };

  // Unified history (recharges + invoice payments). Append on paginated loads.
  useEffect(() => {
    const incoming = stripeClientBillingHistoryData?.history || [];
    if (!stripeClientBillingHistoryData) return;
    // A fresh fetch (no starting_after) resets the list; a load-more appends.
    const isInitial =
      !stripeClientBillingHistoryData?.history ||
      billingHistory.length === 0 ||
      incoming[0]?.id !== billingHistory[0]?.id;
    setBillingHistory(isInitial ? incoming : [...billingHistory, ...incoming]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeClientBillingHistoryData]);

  const handlePaymentPortalOpening = async () => {
    await createClientPaymentIntent({
      userID: user.id,
      stripeCustomerId: user.stripeCustomerId,
    });
    await fetchStripeClientSecret();
    setPaymentMethodEditMode(true);
  };

  const handlePaymentMethodSave = async () => {
    // No-op: we no longer wipe existing cards when adding a new one. Additional
    // cards are retained as backup payment methods for auto-recharge failover.
    return Promise.resolve();
  };

  const fetchCheckoutSession = async () => {
    try {
      const sessionResponse = fetchStripeCheckout();
      const { sessionId } = await sessionResponse;

      const stripe = await stripePromise;
      await stripe.redirectToCheckout({
        sessionId,
      });
    } catch (err) {
      console.error(err);
      AppToasty.show(t("StripeCheckoutFetchFailed"), { type: "danger" });
    }
  };

  const saveCustomerDetails = async (values) => {
    try {
      const result = await saveStripeCustomer({
        name: values.name,
        address: {
          ...values,
          name: undefined,
        },
        isBusiness: !!values.isBusiness,
        businessName: values.isBusiness ? values.businessName : undefined,
        taxId:
          values.isBusiness && values.taxIdValue
            ? { type: values.taxIdType, value: values.taxIdValue }
            : null,
      });
      // Surface a tax-ID rejection (Stripe accepted the rest of the update but
      // the tax ID format was invalid) without failing the whole save.
      const taxIdError = result?.data?.taxIdError;
      if (taxIdError) {
        AppToasty.show(
          `${t("billing.form.taxIdError")}: ${taxIdError}`,
          { type: "danger" },
        );
      }
      const resultFromStripe = await fetchStripeCustomer();
      if (resultFromStripe) {
        setupCustomStripeDetails(resultFromStripe);
      }
      AppToasty.show(t("StripeCustomerSaveSuccess"), { type: "success" });
      isWizard && fetchCheckoutSession();
    } catch (err) {
      console.error("Error occured while saving the customer", err);
      AppToasty.show(t("StripeCustomerSaveFailed"), { type: "danger" });
    }
  };

  const goToPortal = async () => {
    try {
      fetchStripePortal();
    } catch (error) {
      console.error(error);
      AppToasty.show(t("StripeCheckoutFetchFailed"), { type: "danger" });
    }
  };

  useEffect(() => {
    fetchCustomerAddress();
  }, []);

  useEffect(() => {
    if (stripePortalData) {
      const { portalUrl } = stripePortalData;
      if (portalUrl) {
        window.open(portalUrl, "_blank");
      }
    }
  }, [stripePortalData]);

  return isSSO() ? (
    <AppPlaceholderView
      wrapperStyle={{
        width: 335,
        height: "auto",
        minHeight: 200,
        marginVertical: "auto",
        marginHorizontal: "auto",
      }}
      mainCaption={"SSO users cannot register payment methods"}
      subCaption={"Contact your administrator for more information"}
      size={30}
      icon={AppAssets.icons.orbit}
    />
  ) : (
    <BillingDetailsView
      isPaymentMethodEditMode={isPaymentMethodEditMode}
      setPaymentMethodEditMode={setPaymentMethodEditMode}
      stripeCustomerAPIInProgress={stripeCustomerAPIInProgress}
      stripePortalAPIInProgress={stripePortalAPIInProgress}
      paymentMethodAPIInProgress={paymentMethodAPIInProgress}
      stripeClientBillingHistoryAPIInProgress={
        stripeClientBillingHistoryAPIInProgress
      }
      stripeClientSecretAPIInProgress={stripeClientSecretAPIInProgress}
      clientPaymentIntentAPIInProgress={clientPaymentIntentAPIInProgress}
      paymentMethodsAPIInProgress={paymentMethodsAPIInProgress}
      stripeCustomerData={stripeCustomerData}
      stripeClientSecretData={stripeClientSecretData}
      stripeClientBillingHistoryData={stripeClientBillingHistoryData}
      paymentMethodsData={paymentMethodsData}
      stripePromise={stripePromise}
      isWizard={isWizard}
      saveCustomerDetails={saveCustomerDetails}
      saveInProgress={saveInProgress}
      goToPortal={goToPortal}
      handlePaymentPortalOpening={handlePaymentPortalOpening}
      handlePaymentMethodSave={handlePaymentMethodSave}
      onPaymentMethodSave={onPaymentMethodSave}
      setPrimaryPaymentMethod={setPrimaryPaymentMethod}
      removePaymentMethod={removePaymentMethod}
      loadMoreBillingHistory={loadMoreBillingHistory}
      billingHistory={billingHistory}
      credits={credits}
      onRefresh={refreshBillingData}
      onCreditsSaved={() => fetchStripeCustomer()}
    />
  );
};

export default BillingDetailsContainer;
